const Wishlist = (() => {
  const STORAGE_KEY = 'wishlist';

  // ---------- Helpers ----------
  const formatMoney = (cents) => `$${(cents / 100).toFixed(2)}`;

  const outlineHeartSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="24" height="24" aria-hidden="true" focusable="false">
    <path fill="#fff" fill-opacity="0.4" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" d="M16,4.88c.74.74,1.1,1.71,1.1,2.68s-.37,1.93-1.1,2.66l-6,6-6-6c-1.48-1.48-1.47-3.87,0-5.34.74-.74,1.7-1.11,2.67-1.11s1.93.37,2.67,1.11l.66.66.66-.66c1.47-1.47,3.87-1.47,5.34,0Z"/>
  </svg>`;

  const filledHeartSVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="24" height="24" aria-hidden="true" focusable="false">
    <path fill="#000" stroke="none" d="M17.62,7.56c0,1.15-.44,2.22-1.25,3.03l-6,6c-.1.1-.24.16-.37.16s-.27-.06-.37-.16l-6-6c-1.68-1.68-1.68-4.4,0-6.08.81-.82,1.89-1.26,3.04-1.26s2.23.44,3.04,1.26l.29.29.29-.29c1.67-1.68,4.41-1.68,6.08,0,.81.81,1.25,1.89,1.25,3.05Z"/>
  </svg>`;

  const getWishlist = () => JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  const setWishlist = (items) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { count: items.length } }));
  };

  // ---------- Core functions ----------
  const add = (handle) => {
    const list = getWishlist();
    if (!list.includes(handle)) {
      list.push(handle);
      setWishlist(list);
      showToast('Added to wishlist');
    }
  };

  const remove = (handle) => {
    const list = getWishlist().filter(id => id !== handle);
    setWishlist(list);
    showToast('Removed from wishlist');
  };

  const toggle = (handle, btn) => {
    const list = getWishlist();
    const active = list.includes(handle);
    active ? remove(handle) : add(handle);
    updateButton(btn, !active);
    renderWishlistPage();
  };

  const exists = (handle) => getWishlist().includes(handle);

  const updateButton = (btn, liked) => {
    if (!btn) return;
    btn.setAttribute('aria-pressed', liked);
    btn.classList.toggle('wishlist-liked', liked);
    btn.innerHTML = liked ? filledHeartSVG : outlineHeartSVG;
    btn.setAttribute('aria-label', liked ? 'Remove from wishlist' : 'Add to wishlist');
  };

  const showToast = (msg) => {
    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.textContent = msg;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
  };

  const updateBadge = () => {
    const badge = document.querySelector('.wishlist-count-badge');
    const count = getWishlist().length;
    if (badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    }
  };

  // ---------- Bind buttons in a root element ----------
  const initButtons = (root = document) => {
    root.querySelectorAll('.wishlist-heart-button, .wishlist-button').forEach(btn => {
      const handle = btn.dataset.productHandle;
      updateButton(btn, exists(handle));
    });
  };

  // ---------- Delegated click & keyboard handlers ----------
  document.addEventListener('click', e => {
    const btn = e.target.closest('.wishlist-heart-button, .wishlist-button');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.productHandle;
    toggle(handle, btn);
  });

  document.addEventListener('keydown', e => {
    if (!['Enter', ' '].includes(e.key)) return;
    const btn = e.target.closest('.wishlist-heart-button, .wishlist-button');
    if (!btn) return;
    e.preventDefault();
    const handle = btn.dataset.productHandle;
    toggle(handle, btn);
  });

  // ---------- Wishlist page rendering ----------
  const renderWishlistPage = async () => {
    if (!window.location.pathname.includes('/pages/wishlist')) return;

    const wishlist = getWishlist();
    const container = document.querySelector('#wishlist-products');
    if (!container) return;

    if (!Shopify?.customer?.id && wishlist.length > 0) {
      container.innerHTML = '<p>Here’s your wishlist. <a href="/account/login">Log in</a> to save it permanently.</p>';
      return;
    } else if (!Shopify?.customer?.id) {
      container.innerHTML = '<p>Please log in to view your wishlist. <a href="/account/login">Login</a></p>';
      return;
    } else if (!wishlist.length) {
      container.innerHTML = '<p>Your wishlist is empty.</p>';
      return;
    }

    const products = await Promise.all(wishlist.map(async h => {
      try {
        const res = await fetch(`/products/${h}.js`);
        if (!res.ok) throw new Error('Product not found');
        return await res.json();
      } catch {
        console.warn(`Product "${h}" not found or unavailable`);
        return null;
      }
    }));

    const validProducts = products.filter(p => p !== null);

    container.innerHTML = `
      <div class="ai-digital-footprint-products">
        ${validProducts.map(p => `
          <div class="ai-digital-footprint-product" data-product-handle="${p.handle}">
            <div class="ai-digital-footprint-product-image-wrapper" style="position: relative;">
              <img src="${p.images[0]}" alt="${p.title}" class="ai-digital-footprint-product-image" />
              <button
                class="wishlist-heart-button"
                aria-label="Remove from wishlist"
                aria-pressed="true"
                data-product-handle="${p.handle}"
                style="position: absolute; top: 10px; right: 10px; background: transparent; border: none; cursor: pointer;"
              >
                ${filledHeartSVG}
              </button>
            </div>
            <div class="ai-digital-footprint-product-info">
              <h3 class="ai-digital-footprint-product-title h5">${p.title}</h3>
              <div class="ai-digital-footprint-product-price">${formatMoney(p.price)}</div>
              <a href="${p.url}" class="ai-digital-footprint-product-button">View Product</a>
            </div>
          </div>
        `).join('')}
      </div>
    `;

    initButtons(container);
  };

  // ---------- Product recommendations handling ----------
  const initRecommendations = () => {
    document.querySelectorAll('product-recommendations').forEach(section => {
      const observer = new MutationObserver(() => {
        const buttons = section.querySelectorAll('.wishlist-heart-button, .wishlist-button');
        if (buttons.length) {
          initButtons(section); // initialize state (aria, SVG)
          updateBadge();
          observer.disconnect();
        }
      });
      observer.observe(section, { childList: true, subtree: true });
    });
  };

  // ---------- Initializers ----------
  document.addEventListener('DOMContentLoaded', () => {
    updateBadge();
    initButtons();
    renderWishlistPage();
    initRecommendations();
  });

  document.addEventListener('wishlist:updated', () => {
    updateBadge();
    renderWishlistPage();
    initRecommendations();
  });

  // ---------- Public API ----------
  return {
    get: getWishlist,
    add,
    remove,
    toggle,
    exists,
    updateBadge,
    renderWishlistPage,
    initRecommendations
  };
})();
